import React, { useState, useRef, useEffect, useContext } from "react";
import { useDrop } from "react-dnd";
import { ItemTypes } from "./types";
import CustomSelect from "shared/components/FormElements/Select";
import { subtractSecondsFromDate } from "shared/util/time";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import "./dragger.css";
import { checkIsPickUp } from "shared/utility/types";
import MapWithTransferTimesContainer from "components/shared/MapWithTransferTimesContainer";

const DraftService = ({
  id,
  school,
  users,
  name,
  destinationTime,
  driver,
  onDrop,
  otherServices,
  onOptimize,
  onDeleteService,
  onSaveResult,
  onChangeName,
  routeType,
}) => {
  const store = useContext(StoreContext);
  const auth = useContext(AuthContext);

  const { sendRequest } = useHttpClient();

  const mapREF = useRef(null);
  const mapsREF = useRef(null);
  const polylinesREF = useRef([]);
  const polyLineLabelsREF = useRef([]);
  const optimizedREF = useRef(false);
  const [calculatedUsers, setCalculatedUsers] = useState([]);
  const [airportInfo, setAirportInfo] = useState({});
  const [transferName, setTransferName] = useState(name);

  const distancesInformation = useRef([]);

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: ItemTypes.PASSENGER,
    drop: (item) => onDrop(item.id, id),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    type: ItemTypes.VEHICLE,
  });

  const isActive = canDrop && isOver;

  const drawTheOtherService = (serviceId) => {
    const service = otherServices.find((service) => service.id === serviceId);
    if (!service) {
      return;
    }
  };

  const drawPolylines = async () => {
    if (!mapREF.current || !mapsREF.current) {
      return;
    }

    // Clear existing polylines and labels
    clearExistingPolylines();

    const isPickUpService = checkIsPickUp(routeType);
    const routeStops = buildRouteStops(isPickUpService);
    const currentPeople = await processRouteStops(routeStops, isPickUpService);

    // Update state and save results
    setCalculatedUsers(currentPeople);
    onSaveResult(currentPeople, formState.inputs.newdriver.value, transferName);
  };

  const clearExistingPolylines = () => {
    // Clear polylines
    polylinesREF.current.forEach((polyline) => polyline.setMap(null));
    polylinesREF.current = [];

    // Clear labels
    polyLineLabelsREF.current.forEach((label) => label.setMap(null));
    polyLineLabelsREF.current = [];
  };

  const buildRouteStops = (isPickUpService) => {
    const stops = [];

    if (isPickUpService) {
      // Pickup service: School → Passenger1 → Passenger2 → ... → PassengerN → School
      stops.push(...users);
      stops.push(school);
    } else {
      // Drop-off service: School → Passenger1 → Passenger2 → ... → PassengerN
      stops.push(school);
      stops.push(...users);
    }

    return stops;
  };

  const processRouteStops = async (routeStops, isPickUpService) => {
    const currentPeople = [];

    // Process each leg of the journey (from one stop to the next)
    for (let i = 0; i < routeStops.length - 1; i++) {
      const currentStop = routeStops[i];
      const nextStop = routeStops[i + 1];

      // Skip if this is the last leg and it's not a pickup service
      if (i === routeStops.length - 2 && !isPickUpService) {
        break;
      }

      // Get distance information
      const distanceInfo = await getDistanceInfo(currentStop, nextStop);
      if (!distanceInfo) {
        showDistanceError(currentStop, nextStop);
        continue;
      }

      // Process passenger information if current stop is a passenger
      if (isPassenger(currentStop)) {
        const passengerInfo = processPassengerInfo(currentStop, distanceInfo);
        currentPeople.push(passengerInfo);
      }

      // Handle airport info for drop-off service
      if (!isPickUpService && i === 0) {
        setAirportInfo({
          polylineLabel: distanceInfo.label,
          distanceLabel: distanceInfo.distanceText,
          durationLabel: distanceInfo.durationText,
          durationValue: distanceInfo.durationValue,
          distanceValue: distanceInfo.distanceValue,
        });
      }
    }

    // Calculate pickup times for pickup service
    // if (isPickUpService) {
    //   calculatePickupTimes(currentPeople);
    // }

    return currentPeople;
  };

  const isPassenger = (stop) => {
    return stop && stop.id && stop.name; // Basic check if it's a passenger
  };

  const getDistanceInfo = async (origin, destination) => {
    // Check if we already have this distance cached
    let distanceInfo = distancesInformation.current.find((d) => d.fromId === origin.id && d.toId === destination.id);

    if (!distanceInfo) {
      const responseDistanceFromAPI = await fetchDistanceFromAPI(origin, destination);

      if (!responseDistanceFromAPI?.distance?.text) {
        return null;
      }

      distancesInformation.current.push({
        fromId: origin.id,
        toId: destination.id,
        distanceText: responseDistanceFromAPI.distance.text,
        durationText: responseDistanceFromAPI.duration.text.replace("mins", "dk").replace("min", "dk"),
        durationValue: responseDistanceFromAPI.duration.value,
        distanceValue: responseDistanceFromAPI.distance.value,
        label: `${responseDistanceFromAPI.distance.text} (${responseDistanceFromAPI.duration.text
          .replace("mins", "dk")
          .replace("min", "dk")})`,
      });
      distanceInfo = distancesInformation.current.find((d) => d.fromId === origin.id && d.toId === destination.id);
    }

    if (!distanceInfo) {
      return null;
    } else {
      return distanceInfo;
    }
  };

  const fetchDistanceFromAPI = async (origin, destination) => {
    const stopsToAsk = [origin, destination];

    const data = {
      origin: school,
      stops: stopsToAsk,
      type: "100",
    };

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/automation-runner/getdistances`,
        "POST",
        JSON.stringify(data),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      if (response.distances) {
        return response.distances.find((d) => d.from === origin.id && d.to === destination.id).distance.data;
      }
    } catch (err) {
      alert(err);
    }

    return null;
  };

  const showDistanceError = (from, to) => {
    alert(
      `Hata: ${from.name} - ${to.name} arasındaki yol için Google haritalardan yol çıkarılamadı. ` +
        "Okları kullanarak işlemlere devam ediniz."
    );
  };

  const processPassengerInfo = (passenger, distanceInfo) => {
    return {
      ...passenger,
      polylineLabel: distanceInfo.label,
      durationLabel: distanceInfo.durationText,
      distanceLabel: distanceInfo.distanceText,
      durationValue: distanceInfo.durationValue,
      distanceValue: distanceInfo.distanceValue,
    };
  };

  // eslint-disable-next-line no-unused-vars
  const calculatePickupTimes = (currentPeople) => {
    let pickupBeforeSeconds = 0;
    const airportHourMinute = destinationTime.split(" ")[1].substring(0, 5);
    const additionTimeInSeconds = airportHourMinute > "07:00" ? 300 : 0;

    // Calculate pickup times from last passenger to first (reverse order)
    for (let i = currentPeople.length - 1; i >= 0; i--) {
      pickupBeforeSeconds += currentPeople[i].durationValue;
      currentPeople[i].pickupbeforeseconds = pickupBeforeSeconds;
      currentPeople[i].pickupTime = subtractSecondsFromDate(
        destinationTime,
        pickupBeforeSeconds + additionTimeInSeconds
      )
        .split(" ")[4]
        .substring(0, 5);
    }
  };

  useEffect(() => {
    drawPolylines();
    if (users?.length && !optimizedREF.current) {
      //  onOptimize();
      optimizedREF.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const [formState, inputHandler] = useForm(
    {
      newdriver: { value: store.driverOptions.find((option) => option.value === driver.id).value, isValid: true },
    },
    true
  );

  const [formStateKarsilastirma, inputHandlerKarsilastirma] = useForm(
    {
      otherService: { value: null, isValid: true },
      otherServiceUser: { value: null, isValid: true },
    },
    true
  );

  // eslint-disable-next-line no-unused-vars
  const personPickupTimeChangeHandler = (event, personId) => {
    const person = calculatedUsers.find((person) => person.id === personId);
    person.pickupTime = event.target.value;
    setCalculatedUsers([...calculatedUsers]);

    //if(CheckTimeString(person.pickupTime))
    //{
    onSaveResult([...calculatedUsers], formState.inputs.newdriver.value, transferName);
    //}
  };

  // eslint-disable-next-line no-unused-vars
  const transferNameChangeHandler = (event) => {
    setTransferName(event.target.value);
    //if(event.target.value.length > 0)
    // {
    // onSaveResult([...calculatedUsers], formState.inputs.newdriver.value, event.target.value);
    onChangeName(event.target.value);
  };

  const sendToOtherService = () => {
    if (!formStateKarsilastirma.inputs.otherService.value || !formStateKarsilastirma.inputs.otherServiceUser.value) {
      return;
    }

    onDrop(formStateKarsilastirma.inputs.otherServiceUser.value, formStateKarsilastirma.inputs.otherService.value);
  };

  return (
    <div
      ref={drop}
      style={{
        backgroundColor: isActive ? "#fbdd04" : "lightgray",
        padding: "10px",
        margin: "5px",
        marginBottom: "30px",
        borderRadius: "5px",
      }}
    >
      <div style={{ display: "flex", width: "100%", flexDirection: "row" }}>
        <div style={{ width: "25%", display: "flex", flexDirection: "column" }}>
          <input
            style={{
              marginBottom: "10px",
              height: "30px",
              width: "95%",
              borderRadius: "5px",
              paddingLeft: "5px",
              outlineColor: transferName?.length ? "black" : "red",
              borderColor: transferName?.length ? "black" : "red",
            }}
            type="text"
            id={"servicename" + id}
            // onInput={(event) => transferNameChangeHandler(event)}
            defaultValue={transferName}
          />
          <div style={{ marginBottom: "5px" }}>
            Toplam Km:{" "}
            <b id={"servicetotaldistance" + id}>
              {(
                (calculatedUsers.reduce((sum, obj) => sum + (obj?.distanceValue || 0), 0) +
                  (airportInfo?.distanceValue || 0)) /
                1000
              ).toFixed(1)}
            </b>
          </div>
          <div style={{ marginBottom: "10px" }}>
            Toplam Süre:{" "}
            <b id={"servicetotalduration" + id}>
              {(
                (calculatedUsers.reduce((sum, obj) => sum + (obj?.durationValue || 0), 0) +
                  (airportInfo?.durationValue || 0)) /
                60
              ).toFixed(0)}{" "}
              dk
            </b>
          </div>
          <div style={{ marginRight: "5px" }}>
            <CustomSelect
              options={store.driverOptions}
              id={"newdriver" + id}
              isMulti={false}
              onInput={(id, value) => {
                // onSaveResult(calculatedUsers, value, transferName);
                inputHandler(id, value, true);
              }}
              label="Sürücü"
              errorText="En az 1 sürücü seçiniz."
              initialValue={store.driverOptions.find((option) => option.value === driver.id)}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
            ></CustomSelect>
          </div>
          <div>
            {!checkIsPickUp(routeType) && (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                  alignItems: "center",
                  placeContent: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#aaa",
                    fontWeight: "bold",
                    marginRight: "5px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    padding: "10px",
                    width: "80%",
                  }}
                >
                  {school.name} - {destinationTime}
                </div>
                <div style={{ marginBottom: "10px" }}>
                  {" "}
                  {">> "}
                  {airportInfo.polylineLabel} {" >>"}
                </div>
              </div>
            )}

            <button
              style={{
                alignSelf: "flex-start",
                backgroundColor: "#830000",
                borderColor: "#830000",
                color: "white",
                height: "30px",
                borderRadius: "5px",
                marginBottom: "5px",
                marginTop: "20px",
                padding: "20px",
                display: "flex",
                alignItems: "center",
                fontSize: "large",
              }}
              type="button"
              onClick={onOptimize}
            >
              Optimize et
            </button>

            <button
              style={{
                alignSelf: "flex-end",
                backgroundColor: "#830000",
                borderColor: "#830000",
                color: "white",
                height: "30px",
                borderRadius: "5px",
                marginBottom: "5px",
                marginTop: "50px",
                padding: "10px",
                display: "flex",
                alignItems: "center",
              }}
              type="button"
              onClick={onDeleteService}
            >
              Bu aracı sil
            </button>
          </div>
        </div>

        <div style={{ width: "75%" }}>
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            <div
              style={{ display: "flex", width: "100%", flexDirection: "row", columnGap: "10px", alignItems: "center" }}
            >
              <div style={{ width: "38%" }}>
                <CustomSelect
                  options={[
                    { value: null, label: "Seçiniz" },
                    ...otherServices.map((service) => {
                      return { value: service.id, label: service.name };
                    }),
                  ]}
                  id="otherService"
                  isMulti={false}
                  onInput={(id, value) => {
                    drawTheOtherService(value);
                    inputHandlerKarsilastirma(id, value, true);
                  }}
                  label="Karşılaştırılacak Servis"
                  validators={[VALIDATOR_NONE()]}
                ></CustomSelect>
              </div>

              <div style={{ width: "38%" }}>
                <CustomSelect
                  options={[
                    { value: null, label: "Seçiniz" },
                    ...(users ?? []).map((user) => {
                      return { value: user.id, label: user.name };
                    }),
                  ]}
                  id="otherServiceUser"
                  isMulti={false}
                  onInput={(id, value) => {
                    inputHandlerKarsilastirma(id, value, true);
                  }}
                  label="Yolcu"
                  validators={[VALIDATOR_NONE()]}
                ></CustomSelect>
              </div>

              <div style={{ width: "20%" }}>
                <button
                  style={{
                    alignSelf: "flex-start",
                    backgroundColor: "#830000",
                    borderColor: "#830000",
                    color: "white",
                    height: "30px",
                    borderRadius: "5px",
                    marginLeft: "5px",
                    marginTop: "30px",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "large",
                  }}
                  type="button"
                  onClick={sendToOtherService}
                >
                  Gönder
                </button>
              </div>
            </div>
            <div
              style={{
                height: "42rem",
                width: "100%",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <MapWithTransferTimesContainer
                stops={users}
                origin={school}
                type={routeType}
                enablePolyline={true}
                transferStartFinishTime="12:00"
                isFullScreen={true}
                onOptimize={(newStops) => {
                  setCalculatedUsers(newStops);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftService;
